import { Icon } from '@components/primitives/Icon'
import { faCheckSquare } from '@fortawesome/pro-regular-svg-icons/faCheckSquare'
import { formatAddress } from '@lib/utilities/location/formatAddress'

export default function EmrReleaseScroller({
  consent,
}: {
  consent: {
    consentAddressCity: string | null
    consentAddressCountry: string | null
    consentAddressLine1: string | null
    consentAddressState: string | null
    consentAddressZip: string | null
    consentDob: string | null
    consentLegalName: string | null
    consentSignature: string | null // data:image/png;base64,iVBORw0KGgoAA... etc
    consentSSNSuffix: string | null // last 4 digits of SSN
  }
}) {
  const address = formatAddress({
    address: consent.consentAddressLine1,
    city: consent.consentAddressCity,
    country: consent.consentAddressCountry,
    state: consent.consentAddressState,
    zip: consent.consentAddressZip,
  })

  return (
    <div className='relative rounded-lg border border-neutral200'>
      <div className='show-scrollbar grid max-h-48 grid-cols-1 gap-2 overflow-y-scroll px-2 pb-0 pt-2 md:max-h-64 md:p-4 md:pb-0'>
        <h3 className='text-base font-bold'>
          MEDICAL RECORD RELEASE AUTHORIZATION
        </h3>
        <div className='mb-0.5'>
          <h4 className='text-base font-bold'>Part I: Patient Information</h4>
          <p className='inline text-sm'>
            <b className='font-bold'>Patient Legal Name: </b>
            {consent.consentLegalName ?? '******* *******'},{' '}
          </p>
          <p className='inline text-sm'>
            <b className='font-bold'>Date of Birth: </b>
            {consent.consentDob ?? '**/**/****'},{' '}
          </p>
          <p className='inline text-sm'>
            <b className='font-bold'>Address: </b>
            {address ?? '*******'},{' '}
          </p>
        </div>
        <div className='mb-0.5'>
          <h4 className='text-base font-bold'>
            Part II: Recipient Information
          </h4>
          <p className='inline text-sm'>
            <b className='font-bold'>Recipient Organization: </b>
            Power Life Sciences Inc.,{' '}
          </p>
          <p className='inline text-sm'>
            <b className='font-bold'>Address: </b>
            1045 Sansome St, Suite 321, San Francisco, CA 94111,{' '}
          </p>
          <p className='inline text-sm'>
            <b className='font-bold'>Email: </b>
            concierge@withpower.com,{' '}
          </p>
          <p className='inline text-sm'>
            <b className='font-bold'>Fax: </b>
            (866) 854 2184,{' '}
          </p>
        </div>
        <div className='mb-0.5'>
          <h4 className='text-base font-bold'>
            Part III: Records I Authorize to be Disclosed
          </h4>
          <p className='inline text-sm'>
            Full Medical Record from All Providers (i.e., healthcare providers,
            hospitals) including:
          </p>
          <div className='grid grid-cols-1 gap-1 md:grid-cols-3'>
            {[
              'All Medical Notes & Reports',
              'Pathology Reports',
              'Radiology Report and Images',
              'Provider Notes',
              'Genetic Testing Information',
              'Operative Reports',
              'Discharge Summaries',
              'Lab Reports',
            ].map((item, index) => (
              <div className='flex items-center gap-1' key={index}>
                <Icon className='mr-1 bg-success300' icon={faCheckSquare} />
                <p className='text-sm'>{item}</p>
              </div>
            ))}
          </div>

          <p className='inline text-sm'>
            Sensitive Information: State/Federal laws require specific
            authorization to release the following types of information:
          </p>

          {[
            'Sexually Transmitted Diseases',
            'Reproductive Health Care',
            'Gender Affirming Care',
            'Alcohol/Drug Abuse',
            'HIV/AIDS Testing/Treatment',
            'Mental Health (a separate written authorization is required for psychotherapy notes)',
          ].map((item, index) => (
            <div className='flex items-center gap-1' key={index}>
              <Icon className='mr-1 bg-success300' icon={faCheckSquare} />
              <p className='text-sm'>{item}</p>
            </div>
          ))}
        </div>
        <div className='mb-0.5'>
          <h4 className='text-base font-bold'>Part IV: Notice of Rights:</h4>
          <p className='inline text-sm'>I understand: </p>
          <ol className='list-decimal pl-5 text-sm'>
            <li className='text-sm'>
              The information I authorize to be disclosed is protected by
              federal confidentiality and privacy rules (including 42 C.F.R.
              Part 2 and HIPAA) prohibiting further disclosure of this
              information unless further disclosure is expressly authorized by
              my written consent.
            </li>
            <li className='text-sm'>
              I have a right to see and obtain a copy the information described
              on this authorization form in accordance with my Provider’s
              policies.{' '}
            </li>
            <li className='text-sm'>
              I may revoke this authorization by submitting a signed written
              request to my Provider’s privacy officer.{' '}
            </li>
            <li className='text-sm'>
              If I revoke this authorization, the revocation will not have any
              effect on my Provider’s actions taken prior to receiving my
              revocation.{' '}
            </li>
            <li className='text-sm'>
              My treatment, payment, enrollment, or eligibility for benefits
              will not be conditioned on whether I sign this authorization.
            </li>
            <li className='text-sm'>
              Information disclosed to my designated recipient pursuant to this
              authorization may no longer be protected by federal or state laws
              and that such recipient’s legal obligations with respect to such
              received information may differ from the obligations applicable to
              my Providers.{' '}
            </li>
            <li className='text-sm'>
              I may refuse to sign this authorization and my health care,
              payment for my health care, and health care benefits will not be
              affected by my refusal.
            </li>
            <li className='text-sm'>
              If this{' '}
              <Icon className='mr-1 bg-success300' icon={faCheckSquare} /> is
              checked, Power will receive compensation for the use and/or
              disclosure of my requested information.
            </li>
          </ol>
        </div>
        <div className='mb-0.5 flex flex-col gap-1'>
          <h4 className='text-base font-bold'>
            Part V: Authorization &amp; Signature
          </h4>
          <p className='inline text-sm'>
            I hereby authorize my Providers including my doctor, physician,
            medical specialist, psychiatrist, chiropractor, health care
            professional, dentist, optometrist, health plan, hospital, hospice,
            clinic, laboratory, pharmacy or pharmacy benefit manager, medical
            facility, pathologist, or other provider of medical or mental health
            care, as well as any insurance company and the Medical Information
            Bureau Inc. or other health care clearinghouse that has paid for or
            is seeking payment from me for such services to give, disclose and
            release to Power and who is currently serving as such and without
            restriction, all of my individually identifiable health information
            and medical records (collectively, protected health information or
            “PHI”) regarding any past, present or future medical or mental
            health condition, including all information specified in Part III
            above. Additionally, I authorize Power to ask questions and discuss
            my PHI with my Providers in possession of such PHI even if I am
            fully competent to ask questions and discuss this matter at the
            time.
          </p>
          <p className='inline text-sm'>
            By signing this authorization form, I attest I am the patient, am at
            least 18 years old, and authorize the disclosure of PHI to Power as
            specified herein. I understand that unless I submit my written
            revocation, this authorization will automatically expire upon
            satisfaction of the need for disclosure but will in any event expire
            180 days from the date specified below.
          </p>
        </div>
        <div className='mb-0.5 grid grid-cols-1 gap-2 md:grid-cols-3'>
          <div>
            <p className='text-sm'>
              <b className='font-bold'>Signature: </b>
              --------------
            </p>
          </div>
          <div>
            <p className='text-sm'>
              <b className='font-bold'>Patient Name: </b>
              {consent.consentLegalName ?? '*******'}
            </p>
          </div>
          <div>
            <p className='text-sm'>
              <b className='font-bold'>Date: </b>
              {new Date().toLocaleDateString('en-US', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </p>
          </div>
        </div>
        {/* end of Medical Record Release */}
        <h3 className='text-base font-bold'>HIPAA RELEASE AND AUTHORIZATION</h3>
        <div className='mb-0.5'>
          <p className='text-sm'>
            I, {consent.consentLegalName ?? '******* *******'}, hereby authorize
            the following person to act as Agent with regard to the matters
            specified in this Release:
          </p>
        </div>
        <div className='mb-0.5'>
          <p className='inline text-sm'>
            <b className='font-bold'>Recipient Organization: </b>
            Power Life Sciences Inc.,{' '}
          </p>
          <p className='inline text-sm'>
            <b className='font-bold'>Address: </b>
            1045 Sansome St, Suite 321, San Francisco, CA 94111,{' '}
          </p>
          <p className='inline text-sm'>
            <b className='font-bold'>Email: </b>
            concierge@withpower.com,{' '}
          </p>
          <p className='inline text-sm'>
            <b className='font-bold'>Fax: </b>
            (866) 854 2184,{' '}
          </p>
        </div>
        <div className='mb-0.5'>
          <p className='text-sm'>
            This Release and Authorization and all the provisions contained
            herein are effective immediately. I intend for Power Life Sciences,
            Inc. (“Agent”) to be treated as I would be treated with respect to
            my rights regarding the use and disclosure of my individually
            identifiable health information and other medical records. This
            Release authority applies to any information governed by the Health
            Insurance Portability and Accountability Act of 1996 (HIPAA), 42
            U.S.C. Sections 1320d to 1320d-9 and 45 C.F.R. Sections 164.500 to
            164.534, as may be amended from time to time.
          </p>
        </div>
        <div className='mb-0.5'>
          <h4 className='text-base font-bold'>Part I: Authorization</h4>
          <p className='text-sm'>
            I hereby authorize any doctor, physician, medical specialist,
            psychiatrist, chiropractor, health care professional, dentist,
            optometrist, health plan, hospital, hospice, clinic, laboratory,
            pharmacy or pharmacy benefit manager, medical facility, pathologist,
            or other provider of medical or mental health care, as well as any
            insurance company and the Medical Information Bureau Inc. or other
            health care clearinghouse that has paid for or is seeking payment
            from me for such services (referred to herein as a &quot;covered
            entity&quot;), to give, disclose and release to Agent without
            restriction, all of my individually identifiable health information
            and medical records regarding any past, present or future medical or
            mental health condition, including all information relating to the
            diagnosis and treatment of HIV/AIDS, sexually transmitted diseases,
            mental illness, and drug or alcohol abuse. Additionally, this
            disclosure shall include the ability to ask questions and discuss
            this protected health information with the person or entity who has
            possession of the protected health information even if I am fully
            competent to ask questions and discuss this matter at the time. It
            is my intention to give a full authorization to access any protected
            health information to Agent. Health information and medical records
            as indicated above shall be released at my request or at the request
            of Agent named herein as may be needed to assist in my treatment,
            make decisions about my care or for any other reason, at my
            discretion or at the discretion of Agent.
          </p>

          <p className='text-sm'>
            The authority given to Agent shall supersede any prior agreement
            that I may have made with my health care providers to restrict
            access to or disclosure of my individually identifiable health
            information. The individually identifiable health information and
            other medical records given, disclosed, or released to Agent may be
            subject to re-disclosure by Agent and may no longer be protected by
            HIPAA.
          </p>
        </div>
        <div className='mb-0.5'>
          <h4 className='text-base font-bold'>Part II: Termination</h4>
          <p className='text-sm'>
            This Release shall terminate on the first to occur of: (i) two (2)
            years following my death, or (ii) my written revocation actually
            received by the covered entity. Proof of receipt of my written
            revocation may be by certified mail, registered mail, facsimile,
            electronic mail, or any other means evidencing actual receipt by the
            covered entity. This Release shall not be affected by my subsequent
            disability or incapacity. There are no exceptions to my right to
            revoke this Release.
          </p>
        </div>
        <div className='mb-0.5'>
          <h4 className='text-base font-bold'>
            Part III: Release from Liability
          </h4>
          <p className='text-sm'>
            Each covered entity that acts in reliance on this Release shall be
            released from liability that may result from disclosing my
            individually identifiable health information and other medical
            records.
          </p>
        </div>
        <div className='mb-0.5'>
          <h4 className='text-base font-bold'>Part IV: Legal Action</h4>
          <p className='text-sm'>
            I authorize Agent to bring a legal action against a covered entity
            which refuses to accept and recognize this Release. No covered
            entity may condition treatment, payment, enrollment or eligibility
            for benefits on whether I sign this authorization when the
            prohibition on conditioning of authorizations in 45 C.F.R. Section
            164.508(b)(4) applies. Further, to fulfill my intent as expressed
            herein, I authorize Agent to sign any documentation that Agent deems
            necessary or appropriate to secure the disclosure of my individually
            identifiable health information and other medical records.
          </p>
        </div>
        <div className='mb-0.5'>
          <h4 className='text-base font-bold'>
            Part V: Subsequent Disclosure of Information
          </h4>
          <p className='text-sm'>
            Any information disclosed to Agent under this Release may
            subsequently be disclosed to another party by Agent. Agent shall not
            be required to indemnify a covered entity or perform any act if
            information is subsequently disclosed by Agent.
          </p>
        </div>
        <div className='mb-0.5'>
          <h4 className='text-base font-bold'>
            Part VI: Copies and Facsimiles
          </h4>
          <p className='text-sm'>
            Copies or facsimiles of this Release shall be as valid as the
            original Release.
          </p>
        </div>
        <div className='mb-0.5'>
          <h4 className='text-base font-bold'>
            Part VII: Authorization & Signature
          </h4>
          <p className='text-sm'>
            By signing this authorization form, I attest I am the patient, am at
            least 18 years old, and authorize Agent to act on my behalf as
            specified herein.
          </p>
        </div>
        <div className='mb-0.5 grid grid-cols-1 gap-2 md:grid-cols-3'>
          <div>
            <p className='text-sm'>
              <b className='font-bold'>Signature: </b>
              --------------
            </p>
          </div>
          <div>
            <p className='text-sm'>
              <b className='font-bold'>Patient Name: </b>
              {consent.consentLegalName ?? '*******'}
            </p>
          </div>
          <div>
            <p className='text-sm'>
              <b className='font-bold'>Date: </b>
              {new Date().toLocaleDateString('en-US', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </p>
          </div>
        </div>{' '}
        {/* end of HIPAA */}
      </div>
      <div className='absolute bottom-0 left-0 h-16 w-full rounded-b-lg bg-gradient-to-t from-white to-transparent' />
    </div>
  )
}
